import { useCallback, useMemo, useState } from "react"

import { useSouhlAdminMutation } from "@/provider/apollo/Apollo"
import { graphql } from "@/souhl_admin/gql/__generated__"
import { formatJstDateUntilDayByHyphen } from "@/utils/date"

// prettier-ignore
const updateSalonUsageStartDateMutation = graphql(/* GraphQL */ `
	mutation updateSalonUsageStartDate(
		$input: UpdateSalonUsageStartDateInput!
	) {
		updateSalonUsageStartDate(input: $input) {
			salonIds
		}
	}
`)

export const useUsageStartDate = (
	companyId: string,
	salons: readonly { id: number }[],
	onUpdated: () => Promise<unknown>
) => {
	const [selected, setSelected] = useState<Record<number, boolean>>({})
	const [usageStartDateInput, setUsageStartDateInput] = useState<string>(
		formatJstDateUntilDayByHyphen(new Date())
	)

	const [mutate, { loading: saving }] = useSouhlAdminMutation(
		updateSalonUsageStartDateMutation
	)

	const selectedSalons = useMemo(
		() => salons.filter((s) => selected[s.id]),
		[salons, selected]
	)

	const handleToggle = useCallback(
		(salonId: number, checked: boolean): void => {
			setSelected((prev) => ({ ...prev, [salonId]: checked }))
		},
		[]
	)

	const handleToggleAll = useCallback(
		(checked: boolean): void => {
			const next: Record<number, boolean> = {}
			for (const s of salons) {
				next[s.id] = checked
			}
			setSelected(next)
		},
		[salons]
	)

	const runMutation = useCallback(
		(salonIds: number[], usageStartDate: string | null): void => {
			mutate({
				variables: {
					input: {
						companyId,
						salonIds,
						usageStartDate,
					},
				},
			})
				.then(async () => await onUpdated())
				.then(() => {
					setSelected({})
					alert(usageStartDate ? "更新しました" : "クリアしました")
				})
				.catch((error: unknown) => {
					const message =
						error instanceof Error ? error.message : "更新に失敗しました"
					alert(message)
				})
		},
		[companyId, mutate, onUpdated]
	)

	const handleApply = useCallback((): void => {
		if (selectedSalons.length === 0) {
			alert("店舗を選択してください")
			return
		}
		if (!usageStartDateInput) {
			alert("利用開始日を入力してください")
			return
		}
		runMutation(
			selectedSalons.map((s) => s.id),
			usageStartDateInput
		)
	}, [selectedSalons, usageStartDateInput, runMutation])

	const handleClear = useCallback((): void => {
		if (selectedSalons.length === 0) {
			alert("店舗を選択してください")
			return
		}
		runMutation(
			selectedSalons.map((s) => s.id),
			null
		)
	}, [selectedSalons, runMutation])

	return {
		saving,
		selected,
		selectedCount: selectedSalons.length,
		usageStartDateInput,
		setUsageStartDateInput,
		handleToggle,
		handleToggleAll,
		handleApply,
		handleClear,
	}
}
